import "./css/app.css";

import { FullScreen, useFullScreenHandle } from 'react-full-screen';

import { useTranslation } from "react-i18next";

import { useState, useEffect, lazy, Suspense } from "react";

import { backendApi, removeParameterURL } from "@/utils/URL";

const Login = lazy(() => import('@/pages/Login'));
const Recover = lazy(() => import('@/pages/Password/Recover'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));

import { getUser, saveUser, getToken, removeUser, removeToken, applyUserConfig } from '@/utils/User';
import { saveMenus, getMenus, removeMenus } from '@/utils/Menu';

const LanguageModal = lazy(() => import('@/modals/LanguageModal'));

//import Toast from "@/components/Toast";
const Toast = lazy(() => import('@/components/Toast'));

import { setDarkMode } from '@/utils/Settings';
import { checkIfForgot } from '@/utils/Table';

function App({handleLanguage, handleDarkMode}) {

    const { t, i18n } = useTranslation('global');

    // to get fullscreen on the browser
    const fullScreen = useFullScreenHandle();
  
    const apiUrl = backendApi();

    const [ token, setToken ] = useState(getToken());
    const [ user, setUser ] = useState(getUser());

    // check if recovering password
    const [ isForgot, setIsForgot ] = useState(checkIfForgot());
    
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState('success');
    const [toastTimeout, setToastTimeout] = useState(null);

    const [deferredPrompt, setDeferredPrompt] = useState(null);

    useEffect(() => {
        const handleBeforeInstallPrompt = (e) => {            
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // 1. Check for token in URL (cross-project auth)
        const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
        const tokenFromUrl = urlParams.get('token');
        const companyFromUrl = urlParams.get('company');
        
        if(tokenFromUrl){

            const params = {};
            
            if(companyFromUrl){
                params.companyuser = companyFromUrl;
            }

            // Validate before saving to localStorage
            window.axios.get(apiUrl + '/user/current', {
                headers: { Authorization: `Bearer ${tokenFromUrl}` },
                params: params
            })
            .then(response => {
                if(response.data?.result){

                    const userData = response.data.data.user;
                    const menuData = response.data.data.menus;

                    // Success! First clear previous session completely
                    handleLogin(null, null, true);

                    // Then apply the new session
                    handleLogin(tokenFromUrl, userData);
                    saveMenus(menuData);

                }
                else{
                    // Invalid token from URL. Full logout with error message.
                    handleLogin(null, null, false);
                }
            })
            .catch(() => {
                // 401 or network error. Full logout with error message.
                handleLogin(null, null, false);
            });

            // Clean URL to remove the token from sight
            let newUrl = window.location.pathname;
            
            if(window.location.search){
                newUrl = removeParameterURL(window.location.pathname, new URLSearchParams(window.location.search), 'token') + window.location.hash;
            }
            else{

                const hashParts = window.location.hash.split('?');
                const hashBase = hashParts[0];
                const hashSearch = hashParts[1];
                
                if(hashSearch){
                    newUrl += removeParameterURL(hashBase, new URLSearchParams(hashSearch), 'token');
                }
                else{
                    newUrl += window.location.hash;
                }

            }
            
            window.history.replaceState({}, document.title, newUrl);

        }

        return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    }, []);

    // 2. Fetch user data if we have a token but no user object (session recovery)
    useEffect(() => {
        // Skip if we are currently validating a token from URL
        const urlParams = new URLSearchParams(window.location.search || window.location.hash.split('?')[1]);
        if (urlParams.get('token')) return;

        if (token && !user) {
            window.axios.get(apiUrl + '/user/current', {
                headers: { Authorization: `Bearer ${token}` }
            })
            .then(response => {
                if (response.data?.result) {
                    const userData = response.data.data.user;
                    const menuData = response.data.data.menus;
                    
                    // This will fill the user state and save it to storage
                    handleLogin(token, userData);
                    saveMenus(menuData);
                } else {
                    handleLogin(null, null, true);
                }
            })
            .catch(() => {
                handleLogin(null, null, true);
            });
        }
    }, [token, user, apiUrl]);

    useEffect(() => {
        if(user){
            let config = user?.config;
            if(typeof config === 'string'){
                try{ config = JSON.parse(config); } catch(e){ config = {}; }
            }
            if(config){
                if(Object.hasOwn(config, 'language') && config.language){
                    i18n.changeLanguage(config.language);
                }
                if(Object.hasOwn(config, 'darkmode')){
                    setDarkMode(config.darkmode);
                }
            }
        }
    }, [user, i18n]);

    const handleLogin = (token = null,user = null,logout = false, loginAs = false) => {

        if(token && user){

            // if user has config saved, set it
            applyUserConfig(user, i18n, setDarkMode);

            // save parent info
            if(loginAs){

                // Only save current user as original user if NOT ALREADY impersonating
                const existingOriginal = getUser(true);
                if(!existingOriginal){

                    const userOriginal = getUser();
                    if(userOriginal){
                        userOriginal.token = getToken();
                        saveUser(userOriginal,true);
                    }

                    const menusOriginal = getMenus();
                    if(menusOriginal){
                        saveMenus(menusOriginal,true);
                    }

                }

            }
            else{

                // Check if we are currently impersonating and this is an in-session update (e.g. changing company)
                const userOriginal = getUser(true);
                if(userOriginal && String(userOriginal.id) !== String(user.id)){
                    // Still impersonating another user, preserve parent storage
                }
                else{

                    removeToken(true);
                    removeUser(true);       
                    removeMenus(true);         

                }

            }

            setToken(token); // save on variable
            
            user.token = token; // Add token to user for unified storage
            saveUser(user); // save on storage
            setUser(user); // save on variable
        }
        else{

            // if using loginas, remove it
            removeToken(true);
            removeUser(true);

            // remove current user
            setToken(null);
            removeToken();
            removeUser();
            setUser(null);
            setIsForgot(false);

            // remove menus
            removeMenus();
            
            if(!logout){
                sendMessage(t('error.unauthorized'),'error');
            }
        }
    }

    const sendMessage = (message, type = 'success', timeout = null) => {
        setToastMessage(message);
        setToastType(type);        
        setToastOpen(true);
        setToastTimeout(timeout);
    }

    // to go to top when scrolling
    /*
    does not work. it is always false
    const scrollTrigger = useScrollTrigger({
        //target: containerRef.current || window,
        disableHysteresis: true,
        threshold: 100,
    });
    */

    // to change the language
    const [ showLanguage, setShowLanguage ] = useState(false);
    
    const handleChangeLanguage = (iso) => {  

        // close language modal
        setShowLanguage(false);

        // call parent method for hot change
        handleLanguage(iso);
    };

    return (
        <FullScreen handle={fullScreen}>
        {
            toastOpen &&
            <Suspense>
                <Toast 
                    open={toastOpen} 
                    setOpen={setToastOpen}
                    message={toastMessage}
                    type={toastType}
                    timeout={toastTimeout}
                />
            </Suspense>
        }
            <Suspense>
                <LanguageModal 
                    t={t}
                    open={showLanguage} 
                    setOpen={setShowLanguage} 
                    language={i18n.language}
                    handleLanguage={handleChangeLanguage}
                    fullScreen={fullScreen}
                />
            </Suspense>
        
        {
            token && token.length > 0 ?
                <Suspense>
                    <Dashboard 
                        t={t}
                        i18n={i18n}
                        apiUrl={apiUrl} 
                        handleLogin={handleLogin}
                        handleDarkMode={handleDarkMode}
                        token={token}
                        user={user}
                        setUser={setUser}
                        setToken={setToken}
                        sendMessage={sendMessage}  
                        setShowLanguage={setShowLanguage}
                        fullScreen={fullScreen}                      
                        deferredPrompt={deferredPrompt}
                        setDeferredPrompt={setDeferredPrompt}
                    />
                </Suspense>
            :                
                isForgot ? 
                    <Suspense>    
                        <Recover
                            t={t}
                            language={i18n.language}
                            apiUrl={apiUrl}
                            sendMessage={sendMessage}
                            setIsForgot={setIsForgot}
                            setShowLanguage={setShowLanguage}  
                        /> 
                    </Suspense>                   
                :   
                    <Suspense>                 
                        <Login
                            t={t} 
                            language={i18n.language}
                            apiUrl={apiUrl}
                            handleLogin={handleLogin}
                            sendMessage={sendMessage}
                            setShowLanguage={setShowLanguage}                
                            //isVersions={window.location.pathname === '/versions'}
                        /> 
                    </Suspense>               
        }
        </FullScreen>
    );
}

export default App;
